import { useState, useEffect, useRef } from 'react';
import { Bell, X, Check, CheckCheck, Trash2 } from 'lucide-react';
import Button from '../ui/Button';
import IconButton from '../ui/IconButton';
import {
  getUnreadNotifications,
  getAllNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  subscribeToNotifications,
} from '../../lib/notifications';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function NotificationCenter({ isOpen, onClose }: NotificationCenterProps) {
  const [notifications, setNotifications] = useState<any[]>([]);
  const [showUnreadOnly, setShowUnreadOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen, showUnreadOnly]);

  useEffect(() => {
    const unsubscribe = subscribeToNotifications((newNotification) => {
      setNotifications((prev) => [newNotification, ...prev]);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const loadNotifications = async () => {
    setLoading(true);
    if (showUnreadOnly) {
      const unread = await getUnreadNotifications();
      setNotifications(unread);
    } else {
      const all = await getAllNotifications(50);
      setNotifications(all);
    }
    setLoading(false);
  };

  const handleMarkAsRead = async (notificationId: string) => {
    await markNotificationAsRead(notificationId);
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === notificationId ? { ...n, leido: true, leido_at: new Date().toISOString() } : n
      )
    );
  };

  const handleMarkAllAsRead = async () => {
    await markAllNotificationsAsRead();
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, leido: true, leido_at: new Date().toISOString() }))
    );
  };

  const handleDelete = async (notificationId: string) => {
    await deleteNotification(notificationId);
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
  };

  const getNotificationIcon = (tipo: string) => {
    switch (tipo) {
      case 'task_deadline':
        return '⏰';
      case 'budget_status':
        return '💰';
      case 'daily_reminder':
        return '📅';
      case 'timer_reminder':
        return '⏱️';
      case 'project_update':
        return '📁';
      default:
        return '🔔';
    }
  };

  const getRelativeTime = (date: string) => {
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true, locale: es });
    } catch {
      return 'Hace un momento';
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 mt-2 w-96 bg-gray-800 rounded-panel shadow-dropdown border border-gray-700 z-50"
    >
      <div className="p-4 border-b border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <Bell className="w-5 h-5 text-brand-400" />
            <h3 className="text-lg font-semibold text-gray-50">Notificaciones</h3>
          </div>
          <IconButton icon={X} aria-label="Cerrar notificaciones" variant="ghost" size="sm" onClick={onClose} />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowUnreadOnly(true)}
              className={`px-3 py-1 rounded text-sm transition-colors ${
                showUnreadOnly
                  ? 'bg-brand-600 text-white'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              No leídas
            </button>
            <button
              onClick={() => setShowUnreadOnly(false)}
              className={`px-3 py-1 rounded text-sm transition-colors ${
                !showUnreadOnly
                  ? 'bg-brand-600 text-white'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              Todas
            </button>
          </div>

          {notifications.some((n) => !n.leido) && (
            <Button variant="ghost" size="sm" iconLeft={CheckCheck} onClick={handleMarkAllAsRead}>
              Marcar todas
            </Button>
          )}
        </div>
      </div>

      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="p-8 text-center text-gray-400">
            Cargando notificaciones...
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No tienes notificaciones {showUnreadOnly && 'sin leer'}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-4 hover:bg-gray-750 transition-colors ${
                  !notification.leido ? 'bg-gray-800/50' : ''
                }`}
              >
                <div className="flex items-start space-x-3">
                  <span className="text-2xl flex-shrink-0">
                    {getNotificationIcon(notification.tipo)}
                  </span>
                  <div className="flex-1 min-w-0">
                    {notification.titulo && (
                      <p className="text-sm font-medium text-gray-50 mb-1">
                        {notification.titulo}
                      </p>
                    )}
                    <p className="text-sm text-gray-300 break-words">
                      {notification.mensaje}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {getRelativeTime(notification.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center space-x-1 flex-shrink-0">
                    {!notification.leido && (
                      <IconButton
                        icon={Check}
                        aria-label="Marcar como leída"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMarkAsRead(notification.id)}
                      />
                    )}
                    <IconButton
                      icon={Trash2}
                      aria-label="Eliminar notificación"
                      variant="danger"
                      size="sm"
                      onClick={() => handleDelete(notification.id)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {notifications.length > 0 && (
        <div className="p-3 border-t border-gray-700 text-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              window.location.hash = '#notifications';
              onClose();
            }}
          >
            Ver todas las notificaciones
          </Button>
        </div>
      )}
    </div>
  );
}
